import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/*
 * Modal — a centered window over the page.
 *
 * For a whole platform surface completing in place: those pages are built to
 * a wide measure and belong in the middle of the screen, not down one edge.
 * Work that reads at a step's width stays in the Drawer.
 *
 * The window is the same shape as the settings modal: rounded, sized to the
 * viewport with a margin, full-screen on a phone. Only the body scrolls, so
 * the context line and the close control never leave.
 */

export type ModalSize = 'wide' | 'dialog';

const WINDOW: Record<ModalSize, string> = {
  wide: 'sm:h-[min(820px,calc(100vh-2rem))] sm:w-[min(1040px,calc(100vw-2rem))]',
  dialog: 'sm:h-auto sm:max-h-[calc(100vh-2rem)] sm:w-[min(620px,calc(100vw-2rem))]',
};

export interface ModalProps {
  onClose: () => void;
  /** `dialog` fits a short form; `wide` is for a whole surface. */
  size?: ModalSize;
  /** One line of context above the surface: where this came from. */
  context?: React.ReactNode;
  /** Sits beside the context line. */
  badge?: React.ReactNode;
  /** Pinned below the scrolling body. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ onClose, size = 'wide', context, badge, footer, children }) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/30 backdrop-blur-sm"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`flex h-full min-h-0 w-full flex-col overflow-hidden bg-white shadow-[0_24px_64px_rgba(24,25,29,0.24)] sm:rounded-panel ${WINDOW[size]}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-line px-5 py-4 sm:px-[30px]">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            {context && <p className="m-0 truncate text-[13px] leading-[21.5px] text-ink-muted">{context}</p>}
            {badge}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line-strong bg-white text-ink transition-colors hover:bg-canvas-inset"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-[30px]">{children}</div>

        {footer && (
          <div className="shrink-0 border-t border-line bg-white px-5 py-4 sm:px-[30px]">{footer}</div>
        )}
      </div>
    </div>
  );
};
